import { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Bread, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function VerifyOtp() {
  const { verifyOtp, resendOtp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || "");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await verifyOtp(email, otp);
      toast.success("Email verified — welcome!");
      navigate("/");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try {
      await resendOtp(email);
      toast.success("New code sent");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6" data-testid="verify-page">
      <div className="w-full max-w-sm rounded-lg border border-slate-200 bg-white p-8 shadow-sm">
        <div className="mb-6 flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[#EA580C]">
            <Bread size={20} weight="duotone" className="text-white" />
          </div>
          <span className="font-heading text-lg font-bold text-slate-900">BakeFlow</span>
        </div>
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#FFF7ED]">
          <EnvelopeSimple size={24} weight="duotone" className="text-[#EA580C]" />
        </div>
        <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Check your inbox</h2>
        <p className="mt-1 text-sm text-slate-500">Enter the 6-digit code we emailed you. It expires in 10 minutes.</p>

        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="verify-form">
          {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="verify-error">{error}</div>}
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input id="email" data-testid="verify-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="otp">Verification code</Label>
            <Input id="otp" data-testid="verify-otp-input" required maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} placeholder="000000" className="text-center text-lg tracking-[0.5em] font-semibold" />
          </div>
          <Button type="submit" disabled={loading || otp.length !== 6} data-testid="verify-submit-button" className="w-full bg-[#EA580C] font-semibold text-white hover:bg-[#C2410C]">
            {loading ? "Verifying…" : "Verify & sign in"}
          </Button>
        </form>

        <div className="mt-6 flex items-center justify-between text-sm">
          <button onClick={resend} data-testid="resend-otp-button" className="font-semibold text-[#EA580C] hover:text-[#C2410C]">Resend code</button>
          <Link to="/login" className="text-slate-500 hover:text-slate-700">Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
